import { useEffect, useState } from 'react';
import { useFetchAffectedUrlsQuery, useLazyFetchAffectedUrlsQuery } from '../service';
import { getUrlStringFromObjectEntries } from '../../utils';
import { SERVICEABLE_URLS_ON_PAGE, SERVICEABLE_URLS_POLLING_TIME_MS } from '../../settings';
import { FetchAffectedUrlsResponse } from '../types';
import useBatchUrlAnalysisProcessesPolling from '../../process/hooks/useBatchUrlAnalysisProcessesPolling';
import { ServiceableUrl } from '../../serviceableUrls/types';

type Props = {
    websiteGuid: string;
    seoTestKey: string;
};

export default function useFetchAffectedUrlsPage({ websiteGuid, seoTestKey }: Props) {
    const [lastEvaluatedKey, setLastEvaluatedKey] = useState<Object | null>(null);

    // Urls returned from the hook
    const [accumulatedUrls, setAccumulatedUrls] = useState<ServiceableUrl[]>([]);

    // For automatic re-loading of pages up until the current one
    const [currentPage, setCurrentPage] = useState<number>(1);

    const { isRunningBatchAnalysis } = useBatchUrlAnalysisProcessesPolling(websiteGuid);

    // First page of urls fetching independently -> specific entry in rtk cache -> on invalidate this refetch
    const {
        data: firstPageAffectedUrls,
        isFetching: isFetchingAffectedUrlsFirstPage,
        isLoading: isLoadingAffectedUrlsFirstPage,
    } = useFetchAffectedUrlsQuery(
        {
            w_guid: websiteGuid,
            seo_test_key: seoTestKey,
            limit: SERVICEABLE_URLS_ON_PAGE,
        },
        // If we are running an analysis, refetch if the data is older than 30 seconds
        { refetchOnMountOrArgChange: isRunningBatchAnalysis ? SERVICEABLE_URLS_POLLING_TIME_MS / 1000 : false }
    );

    // n-th page of urls
    const [fetchAffectedUrlsPage, { isFetching: isFetchingAffectedUrlsNextPage }] = useLazyFetchAffectedUrlsQuery();

    const fetchPage = async (lastEvaluatedKey: Object) => {
        const fetchUrlsPromise = fetchAffectedUrlsPage(
            {
                w_guid: websiteGuid,
                seo_test_key: seoTestKey,
                limit: SERVICEABLE_URLS_ON_PAGE,
                last_evaluated_key: getUrlStringFromObjectEntries(lastEvaluatedKey),
            },
            !isRunningBatchAnalysis
        );

        fetchUrlsPromise.unsubscribe();
        const nextPage = await fetchUrlsPromise.unwrap();
        return nextPage;
    };

    // On load more fetch next page separately and add to accumulated urls
    const handleLoadMore = async () => {
        if (lastEvaluatedKey) {
            try {
                const nextPage = await fetchPage(lastEvaluatedKey);

                setAccumulatedUrls([...accumulatedUrls, ...nextPage.data]);
                setLastEvaluatedKey(nextPage.meta.last_evaluated_key);
                setCurrentPage(currentPage + 1);
            } catch (error) {}
        }
    };

    // Whenever the first page refetch, replace everything with the new first page
    useEffect(() => {
        let shouldSetState = true;

        const reloadPages = async (firstPage: FetchAffectedUrlsResponse) => {
            let currentLastEvaluatedKey = firstPage.meta.last_evaluated_key;
            let currentUrls = firstPage.data;

            for (let page = currentPage; page > 1 && currentLastEvaluatedKey; page--) {
                const nextPage = await fetchPage(currentLastEvaluatedKey);

                if (nextPage) {
                    currentUrls = [...currentUrls, ...nextPage.data];
                    currentLastEvaluatedKey = nextPage.meta.last_evaluated_key;
                }
            }

            if (shouldSetState) {
                setAccumulatedUrls(currentUrls);
                setLastEvaluatedKey(currentLastEvaluatedKey);
            }
        };

        if (!isFetchingAffectedUrlsFirstPage && firstPageAffectedUrls?.data) {
            reloadPages(firstPageAffectedUrls);
        }

        return () => {
            shouldSetState = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [firstPageAffectedUrls, isFetchingAffectedUrlsFirstPage]);

    return {
        affectedUrls: accumulatedUrls,
        isFetchingAffectedUrlsNextPage,
        isLoadingAffectedUrlsFirstPage,
        hasMore: lastEvaluatedKey !== null,
        handleLoadMore,
    };
}
